import type { Paginated, Task } from '@/shared/types/task';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import {
  completeTask,
  createTask,
  deleteTask,
  fetchTasks,
  fetchTasksCalendar,
  getTask,
  updateTask,
} from '@/entities/task/api/tasks';
import { getApiErrorMessage } from '@/shared/api';
import { useTaskSelectionModalStore } from '@/entities/task/model/stores/task-selection-modal';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters: ANY) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  allTasks: () => [...taskKeys.all, 'all'] as const,
};

/** Корни query-ключей всех представлений задач: списки, доски, детали. */
const TASK_QUERY_ROOTS = [
  'tasks', // taskKeys.all/lists/list/details/detail + useTasks
  'task', // одиночная деталь ['task', id]
  'tasks-board', // канбан-доска
  'module-tasks', // задачи модуля
  'department-tasks', // задачи отдела
  'projects-plan', // проекты с задачами
];

/**
 * Инвалидирует кэши задач во всех списках/досках/деталях сразу. Зовём после
 * любой смены состояния задачи (завершить, старт/пауза, возобновить, удалить,
 * принять/отклонить), чтобы статус обновился везде, а не только на текущем
 * экране. Один общий помощник — чтобы не забывать отдельные ключи по местам.
 */
export function invalidateTaskCaches(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const root = query.queryKey?.[0];
      return typeof root === 'string' && TASK_QUERY_ROOTS.includes(root);
    },
  });
}

export function useAllTasks({
  isEnabled = true,
}: { isEnabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.list({ all: true }),
    queryFn: () =>
      fetchTasks({
        perPage: 1000,
        page: 1,
      }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (data: Paginated<Task>) => data.items,
    enabled: isEnabled,
  });
}
export function useTasks(params: ANY = {}, options: ANY = {}) {
  return useQuery<Paginated<Task>, Error>({
    queryKey: [...taskKeys.list(params), params.selectedDate],
    queryFn: () => fetchTasks(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Данные для вида «Календарь» — облегчённый /tasks/calendar вместо тяжёлого
 *  полного /tasks/list. Ключ под корнем 'tasks', поэтому invalidateTaskCaches
 *  обновляет календарь вместе с остальными представлениями. */
export function useTasksCalendar(params: ANY = {}, options: ANY = {}) {
  return useQuery<Paginated<Task>, Error>({
    queryKey: [...taskKeys.all, 'calendar', params],
    queryFn: () => fetchTasksCalendar(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useTask(id: string) {
  const queryKey = taskKeys.detail(id);

  return useQuery<Task | undefined, Error>({
    queryKey,
    enabled: !!id,
    // главное изменение здесь
    queryFn: async () => {
      const res = await getTask(id);
      // предполагаем, что res.core по структуре = Task
      return res?.core as Task;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useTaskPrefetching() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const commonFilters = [
      { page: 1, perPage: 10, status: '-5' },
      { page: 1, perPage: 10 },
      { page: 1, perPage: 10, status: '2' },
    ];

    commonFilters.forEach((filters) => {
      queryClient.prefetchQuery({
        queryKey: taskKeys.list(filters),
        queryFn: () => fetchTasks(filters),
      });
    });
  }, [queryClient]);
}

export function useTaskActions() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      invalidateTaskCaches(queryClient);
    },
    onError: (error) => {
      // 413 (вложения не пролезли в лимит тела) бэк объясняет текстом с
      // размером и пределом — показываем его, а не общее «не удалось».
      toast.error(getApiErrorMessage(error, 'Не удалось создать задачу'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ANY }) =>
      updateTask(id, payload),

    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });

      const previous = queryClient.getQueriesData({
        queryKey: taskKeys.lists(),
      });

      const newDeadline =
        payload.DEADLINE === '' || payload.DEADLINE === null
          ? null
          : payload.DEADLINE;

      previous.forEach(([key, old]) => {
        if (!old) {
          return;
        }

        queryClient.setQueryData(key, (prev: ANY) => {
          if (!prev?.items) {
            return prev;
          }

          return {
            ...prev,
            items: prev.items.map((t: Task) =>
              t.id === id
                ? {
                    ...t,
                    dueDate: newDeadline,
                    assigneeId: payload.RESPONSIBLE_ID ?? t.assigneeId,
                  }
                : t
            ),
          };
        });
      });

      return { previous };
    },

    onSuccess: () => {
      invalidateTaskCaches(queryClient);
    },

    onError: (_err, _variables, context) => {
      if (!context?.previous) {
        return;
      }

      context.previous.forEach(([key, old]) => {
        queryClient.setQueryData(key, old);
      });

      toast.error('Не удалось обновить задачу');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      invalidateTaskCaches(queryClient);
    },
    onError: () => {
      toast.error('Не удалось удалить задачу');
    },
  });

  return {
    createTask: createMutation.mutateAsync,
    updateTask: updateMutation.mutateAsync,
    deleteTask: deleteMutation.mutateAsync,
    isLoading:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
  };
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  const { stopTask } = useTaskTimerStore();
  const { openModal } = useTaskSelectionModalStore();

  const mutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: string; silent?: boolean }) =>
      await completeTask(taskId),
    onSuccess: (data, variables) => {
      // Останавливаем задачу в таймере (если она активна)
      stopTask(variables.taskId);

      // Открываем модалку выбора следующей задачи
      if (!variables.silent) {
        openModal();
      }

      toast.success('Задача успешно завершена');
      invalidateTaskCaches(queryClient);
    },
    onError: (error: ANY) => {
      console.error('Ошибка при завершении задачи:', error);
      toast.error('Не удалось завершить задачу');
    },
  });

  return {
    completeTask: mutation.mutateAsync,
    isLoading: mutation.isPending,
  };
}
